import { alpha, Theme } from '@mui/material/styles'
import { ChipProps } from '@mui/material/Chip'
//
import { onHover, onHoverOverTint } from '../interaction'
import { CloseIcon } from './CustomIcons'

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const

export default function Chip(theme: Theme) {
  const isLight = theme.palette.mode === 'light'

  const rootStyle = (ownerState: ChipProps) => {
    const defaultColor = ownerState.color === 'default'

    const filledVariant = ownerState.variant === 'filled'

    const outlinedVariant = ownerState.variant === 'outlined'

    const softVariant = ownerState.variant === 'soft'

    const defaultStyle = {
      borderRadius: '8px',
      '.MuiChip-icon': {
        fontSize: 16,
      },
      ...(defaultColor && {
        '& .MuiChip-avatar': {
          color: theme.palette.text[isLight ? 'secondary' : 'primary'],
          backgroundColor: alpha(theme.palette.grey[500], 0.48),
        },
        // OUTLINED — transparent at rest, so it takes the neutral wash instead of
        // the tint MUI computes from the colour prop.
        ...(outlinedVariant && {
          border: `solid 1px ${alpha(theme.palette.grey[500], 0.32)}`,
          ...onHover({ backgroundColor: theme.palette.action.hover }),
        }),
        // SOFT — the fill stays and the wash lands on top of it, so a chip darkens by
        // the same step as every other control instead of jumping to alpha 0.32.
        ...(softVariant && {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.grey[500], 0.16),
          ...onHoverOverTint(theme, alpha(theme.palette.grey[500], 0.16)),
        }),
      }),
      /**
       * Amber is a background colour only, same rule as Button.ts: as text or
       * a border it lands at ~1.5:1 on white and can never pass, so
       * `secondary` on the variants that colour the label (outlined's border
       * + text, soft's tint) falls back to ink. Filled keeps the amber fill
       * with ink text (~8.8:1) — the brand mark — untouched.
       */
      ...((outlinedVariant || softVariant) &&
        ownerState.color === 'secondary' && {
          color: theme.palette.text.primary,
          ...(outlinedVariant && { borderColor: theme.palette.divider }),
        }),
    }

    const colorStyle = COLORS.map(color => ({
      ...(ownerState.color === color && {
        '& .MuiChip-avatar': {
          color: theme.palette[color].lighter,
          backgroundColor: theme.palette[color].dark,
        },
        // FILLED
        ...(filledVariant && {
          '& .MuiChip-deleteIcon': {
            color: alpha(theme.palette[color].contrastText, 0.56),
            '&:hover': {
              color: theme.palette[color].contrastText,
            },
          },
        }),
        // SOFT
        ...(softVariant && {
          color: theme.palette[color][isLight ? 'dark' : 'light'],
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          fontWeight: 600,
          ...onHoverOverTint(theme, alpha(theme.palette[color].main, 0.16)),
          '& .MuiChip-deleteIcon': {
            color: alpha(theme.palette[color][isLight ? 'dark' : 'light'], 0.48),
            '&:hover': {
              color: theme.palette[color].dark,
            },
          },
        }),
      }),
    }))

    return [...colorStyle, defaultStyle]
  }

  return {
    MuiChip: {
      defaultProps: {
        deleteIcon: <CloseIcon />,
        // The house chip. Set here rather than repeated at ~200 call sites; pass variant or size
        // explicitly only where a chip is deliberately different.
        variant: 'soft',
        size: 'small',
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: ChipProps }) => rootStyle(ownerState),
      },
    },
  }
}
